import React, { useEffect, useMemo, useState } from "react";
import { Box, Tooltip, Typography, Stack, Fade, useTheme, alpha } from "@mui/material";
import WifiIcon from "@mui/icons-material/Wifi";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import SpeedIcon from "@mui/icons-material/Speed";
import RouterIcon from "@mui/icons-material/Router";

// ---- Types
type EffectiveConnectionType = "slow-2g" | "2g" | "3g" | "4g" | "unknown";

type NetworkInformation = {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  addEventListener?: (type: string, listener: any) => void;
  removeEventListener?: (type: string, listener: any) => void;
};

function getConnection(): NetworkInformation | undefined {
  if (typeof navigator === "undefined") return undefined;
  const nav = navigator as any;
  return nav.connection || nav.mozConnection || nav.webkitConnection || undefined;
}

// ---- Premium Style Mapping
const statusConfig: Record<
  EffectiveConnectionType | "offline",
  { color: string; label: string; blur: string }
> = {
  "4g":      { color: "#10b981", label: "Stable",   blur: "rgba(16, 185, 129, 0.15)" },
  "3g":      { color: "#f59e0b", label: "Fair",     blur: "rgba(245, 158, 11, 0.15)" },
  "2g":      { color: "#ef4444", label: "Poor",     blur: "rgba(239, 68, 68, 0.15)" },
  "slow-2g": { color: "#ef4444", label: "Weak",     blur: "rgba(239, 68, 68, 0.15)" },
  "unknown": { color: "#6b7280", label: "Checking", blur: "rgba(107, 114, 128, 0.15)" },
  "offline": { color: "#6366f1", label: "Offline",  blur: "rgba(99, 102, 241, 0.15)" },
};

export const ConnectionStatus: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const theme = useTheme();
  const [isOnline, setIsOnline] = useState(() => (typeof navigator !== "undefined" ? navigator.onLine : true));
  const [type, setType] = useState<EffectiveConnectionType>("unknown");
  const [details, setDetails] = useState<{ downlink?: number; rtt?: number; saveData?: boolean }>({});

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    const conn = getConnection();
    const apply = () => {
      if (!conn) return;
      const raw = (conn.effectiveType || "").toLowerCase();
      const normalized = (["slow-2g", "2g", "3g", "4g"].includes(raw) ? raw : "unknown") as EffectiveConnectionType;
      setType(normalized);
      setDetails({ downlink: conn.downlink, rtt: conn.rtt, saveData: conn.saveData });
    };

    if (conn) {
      apply();
      conn.addEventListener?.("change", apply);
    }
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      conn?.removeEventListener?.("change", apply);
    };
  }, []);

  const currentStatus = isOnline ? statusConfig[type] : statusConfig["offline"];

  const TooltipContent = useMemo(() => (
    <Box sx={{ p: 1, minWidth: 180 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" sx={{ color: alpha("#fff", 0.6), fontWeight: 700, letterSpacing: 1 }}>
            NETWORK STATUS
          </Typography>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: currentStatus.color, boxShadow: `0 0 8px ${currentStatus.color}` }} />
        </Stack>
        
        <Stack spacing={1}>
          <DetailRow icon={<SpeedIcon sx={{ fontSize: 14 }} />} label="Downlink" value={details.downlink ? `${details.downlink} Mbps` : "--"} />
          <DetailRow icon={<RouterIcon sx={{ fontSize: 14 }} />} label="Latency" value={details.rtt ? `${details.rtt} ms` : "--"} />
        </Stack>

        {details.saveData && (
          <Box sx={{ mt: 1, p: 0.5, bgcolor: alpha(currentStatus.color, 0.1), borderRadius: 1, border: `1px solid ${alpha(currentStatus.color, 0.2)}` }}>
            <Typography variant="caption" sx={{ color: currentStatus.color, display: 'block', textAlign: 'center', fontSize: '0.65rem', fontWeight: 700 }}>
              DATA SAVER ACTIVE
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  ), [currentStatus, details]);

  return (
    <Tooltip 
      title={TooltipContent} 
      arrow 
      TransitionComponent={Fade}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "rgba(15, 23, 42, 0.9)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 2,
            boxShadow: "0 10px 15px -3px rgba(0,0,0,0.4)"
          }
        }
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1.5,
          px: 1.5,
          py: 0.75,
          borderRadius: "20px",
          cursor: "pointer",
          transition: "all 0.2s ease",
          bgcolor: theme.palette.mode === 'dark' ? alpha("#fff", 0.03) : alpha("#000", 0.03),
          border: `1px solid ${alpha(currentStatus.color, 0.2)}`,
          "&:hover": {
            bgcolor: alpha(currentStatus.color, 0.08),
            borderColor: alpha(currentStatus.color, 0.4),
            transform: "translateY(-1px)"
          }
        }}
      >
        {/* Pulsing Indicator Dot */}
        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: currentStatus.color,
              zIndex: 2
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: currentStatus.color,
              animation: "pulse 2s infinite",
              zIndex: 1,
              "@keyframes pulse": {
                "0%": { transform: "scale(1)", opacity: 0.8 },
                "70%": { transform: "scale(3)", opacity: 0 },
                "100%": { transform: "scale(1)", opacity: 0 },
              }
            }}
          />
        </Box>

        {!compact && (
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: "0.8rem", 
              fontWeight: 600, 
              color: theme.palette.text.primary,
              letterSpacing: 0.2
            }}
          >
            {currentStatus.label}
          </Typography>
        )}
        
        {isOnline ? (
          <WifiIcon sx={{ fontSize: 16, color: alpha(theme.palette.text.primary, 0.4) }} />
        ) : (
          <WifiOffIcon sx={{ fontSize: 16, color: currentStatus.color }} />
        )}
      </Box>
    </Tooltip>
  );
};

// Helper Component for Tooltip Rows
const DetailRow = ({ icon, label, value }: { icon: React.ReactNode, label: string, value: string }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Stack direction="row" spacing={1} alignItems="center" sx={{ color: alpha("#fff", 0.5) }}>
      {icon}
      <Typography sx={{ fontSize: "0.7rem" }}>{label}</Typography>
    </Stack>
    <Typography sx={{ fontSize: "0.7rem", color: "#fff", fontWeight: 600 }}>{value}</Typography>
  </Stack>
);